/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { isEqual, isEmpty, intersection } from 'lodash'

import {
  Button,
  Heading,
  Select,
  Spinner,
  Checkbox,
  IdentityPaymentsEdit,
  Warning
} from '../'

import {
  decodeBase64,
  isPositiveNumberInput
} from '../../utils'

import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

class DeductionIdentityPaymentsEdit extends React.Component {
  static propTypes = {
    deductionId: PropTypes.string.isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    identityId: PropTypes.string.isRequired,
    identityForName: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    viewMode: PropTypes.func,
    onSave: PropTypes.func.isRequired
  }

  state = {
    paymentsToSave: {},  // will hold payment ids as key with object values containing amount & reason for change
    refetch: false,
    reduceAllPayments: false,
    reduceAllPaymentsReason: ''
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }
  }

  saveChanges = (viewMode = true) => {
    let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
    let errorOccurredDuringPaymentSave = false

    Object.entries(paymentsToSave).forEach(([ paymentId, changedValuesObj ]) => {
      let input = {
        id: paymentId,
        amount: changedValuesObj.amount,
        state: 'changed'
      }
      if (changedValuesObj.reason) {
        input.statusReason = changedValuesObj.reason
      }
      this.props.savePayment({
        variables: { input },
        optimisticResponse: {
          paymentMutate: {
            payment: Object.assign({ __typename: 'PaymentNode' }, input),
            __typename: 'Mutation'
          }
        }
      })
      .then(
        () => console.info(`payment (${paymentId}) changes successfully saved`),
        (error) => {
          console.error('payment changes save failed', paymentId, error)
          errorOccurredDuringPaymentSave = true
        }
      )
      .catch((error) => {
        console.error('network error', error)
        errorOccurredDuringPaymentSave = true
      })
      .then(() => {
        this.setState({
          paymentsToSave: {},
          refetch: false,
          reduceAllPayments: false,
          reduceAllPaymentsReason: ''
        })

        if (viewMode) {
          this.props.viewMode()
        }
        if (this.props.onSave) {
          this.props.onSave(this.props.identityId, errorOccurredDuringPaymentSave)
        }
      })
    })
  }

  hasAmountChanged = (paymentId, amount) => (
    this.props.data.payments.edges.find(({ node: payment }) => (
      payment.id === paymentId && payment.amount !== amount
    ))
  )

  handlePaymentAmountChange = (amount, payment, bulkUpdateToAmount, callback) => {  // 'bulkUpdateToAmount' updates multiple payments to have the same payment.amount value
    if (bulkUpdateToAmount) {
      let payments = payment  // if 'bulkUpdateToAmount' is true, the payment arg holds an array of payment ids
      if (intersection(payments, Object.keys(this.state.paymentsToSave)).length > 0) {
        let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
        payments.forEach(p => {
          // save if amount input is a number and positive
          if (isPositiveNumberInput(amount) && this.hasAmountChanged(p, amount)) {
            paymentsToSave[p].amount = amount
          } else {
            delete paymentsToSave[p]
          }
        })
        if (callback) {
          this.setState({ paymentsToSave }, callback())
        } else {
          this.setState({ paymentsToSave })
        }
      } else {
        let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
        // save if amount input is a number and positive
        payments.forEach(p => {
          // save if amount input is a number and positive
          if (isPositiveNumberInput(amount) && this.hasAmountChanged(p, amount)) {
            paymentsToSave[p] = { amount }
          } else {
            delete paymentsToSave[p]
          }
        })
        if (callback) {
          this.setState({ paymentsToSave }, callback())
        } else {
          this.setState({ paymentsToSave })
        }
      }
    } else {
      if (this.state.paymentsToSave[payment]) {
        let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
        // save if amount input is a number and positive
        if (isPositiveNumberInput(amount) && this.hasAmountChanged(payment, amount)) {
          paymentsToSave[payment].amount = amount
          if (callback) {
            this.setState({ paymentsToSave }, callback())
          } else {
            this.setState({ paymentsToSave })
          }
        } else {
          delete paymentsToSave[payment]
          this.setState({ paymentsToSave })
        }
      } else {
        let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
        // save if amount input is a number and positive
        if (isPositiveNumberInput(amount) && this.hasAmountChanged(payment, amount)) {
          paymentsToSave[payment] = { amount }
          if (callback) {
            this.setState({ paymentsToSave }, callback())
          } else {
            this.setState({ paymentsToSave })
          }
        }
      }
    }
  }

  handlePaymentReasonChange = (reason, payment, bulkUpdateToReason) => {  // 'bulkUpdateToReason' updates multiple payments to have the same payment.statusReason value
    if (bulkUpdateToReason) {
      let payments = payment  // if 'bulkUpdateToReason' is true, the payment arg holds an array of payment ids
      let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
      payments.forEach(p => {
        if (this.state.paymentsToSave[p]) {
          if (reason !== null) {
            paymentsToSave[p].reason = reason
          } else {
            delete paymentsToSave[p].reason
          }
        }
        this.setState({ paymentsToSave })
      })
    } else {
      if (this.state.paymentsToSave[payment]) {
        let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
        paymentsToSave[payment].reason = reason
        this.setState({ paymentsToSave })
      }
    }
  }

  reduceAll = (payments) => {
    let paymentsToSave = {}
    payments.forEach(({ node: payment }) => {
      if (payment.amount > 0) {
        paymentsToSave[payment.id] = { amount: 0 }
      }
    })
    this.setState({ paymentsToSave })
  }

  resetReduceAll = (payments) => {
    let paymentsToSave = {}
    if (!isEmpty(this.state.paymentsToSave)) {
      payments.forEach(({ node: payment }) => {
        if (paymentsToSave[payment.id] && paymentsToSave[payment.id].amount === 0) {
          delete paymentsToSave[payment.id]
        }
      })
      if (!isEqual(this.state.paymentsToSave, paymentsToSave)) {
        return paymentsToSave
      }
    }

    return paymentsToSave
  }

  handleReduceAllReasonChange = (payments, reason) => {
    let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
    Object.keys(paymentsToSave).forEach(paymentId => (
      paymentsToSave[paymentId].reason = reason
    ))
    this.setState({ paymentsToSave })
  }

  resetToInitialAmounts = (payments, previouslySaved = true) => {
    // 'previouslySaved' used to distinguish between payments already saved being reset through a 'reset to initial'-button-click,
    //  and funeral cover payments not yet saved but reverted to initial values by undoing an initial 'Make R0 for this deduction' selection
    let paymentsToSave = {}
    payments.forEach(({ node: payment }) => {
      if (previouslySaved) {
        paymentsToSave[payment.id] = {
          amount: payment.originalAmount,
          reason: 'Changed then reset'
        }
      } else {
        paymentsToSave[payment.id] = {
          amount: payment.originalAmount
        }
        delete paymentsToSave[payment.id].reason
      }
    })
    if (previouslySaved) {
      this.setState({ paymentsToSave }, () => this.saveChanges(false))
    } else {
      this.setState({ paymentsToSave })
    }
  }

  getNonZeroChangedPayment = payment => (
    payment.node.originalAmount > 0 &&
    payment.node.amount !== payment.node.originalAmount
  )

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' type='brand' />
    }

    if (this.props.data.error) {
      return (
        <Warning className='slds-p-around_medium'>
          {`An error occurred loading payments${this.props.identityForName ? ` for ${this.props.identityForName}` : ''}.`}
          <Button
            type='reset'
            onClick={this.props.data.refetch}
            className='slds-m-left_medium'>
            Retry
          </Button>
        </Warning>
      )
    }

    let payments = this.props.data.payments.edges
    let identity = payments.length > 0
      ? payments[0].node.identityFor
      : null

    let paymentsByEmployee = payments.filter(payment => payment.node.source === 'CLIENT')
    let paymentsByEmployer = payments.filter(payment => payment.node.source === 'EMPLOYER')
    let paymentAmountsSummed = [ ...paymentsByEmployer, ...paymentsByEmployee ].length > 0
      ? payments
          .map(payment => payment.node.amount)
          .reduce((a, b) => a + b)
      : 0

    return (
      <div>
        <div className='slds-grid slds-wrap'>
          <div className='slds-col slds-p-top_medium'>
            <Heading marginBottom={6}>
              <b>Change payment amounts for:</b>
            </Heading>
            <Heading marginBottom={0}>
              <div className='slds-col slds-p-bottom_large'>
                <div className='slds-grid slds-grid_pull-padded-medium'>
                  <div className='slds-p-horizontal_large'>
                    {this.props.identityForName}
                  </div>
                  <div className='slds-p-horizontal_large'>
                    {identity
                      ? identity.identityValue
                      : ''
                    }
                  </div>
                  <div className='slds-p-horizontal_large'>
                    {identity && identity.employee
                      ? identity.employee.employeeNumber
                      : ''
                    }
                  </div>
                </div>
              </div>
            </Heading>
          </div>
          <div className='slds-col slds-p-top_medium slds-m-bottom_large slds-text-align_right slds-m-top_large'>
            <Button
              minWidth={150}
              onClick={() => this.props.viewMode(this.props.identityId)}>
              CANCEL
            </Button>
            <Button
              type='brand'
              minWidth={175}
              disabled={Object.keys(this.state.paymentsToSave).length === 0 ||
                !Object.values(this.state.paymentsToSave).every((payment) => payment.hasOwnProperty('reason'))}
              onClick={this.saveChanges}>
              SAVE CHANGES
            </Button>
          </div>
        </div>
        <div className='slds-grid slds-size_1-of-1 slds-m-left_medium slds-p-bottom_large'>
          <div className='slds-grid slds-m-top_large'>
            <Checkbox
              onChange={() => this.setState(prevState => (
                prevState.reduceAllPayments
                  ? {
                      reduceAllPayments: !prevState.reduceAllPayments,
                      reduceAllPaymentsReason: '',
                      paymentsToSave: this.resetReduceAll(payments)
                    }
                  : {
                      reduceAllPayments: !prevState.reduceAllPayments,
                      reduceAllPaymentsReason: ''
                    }
              ))}
              disabled={paymentAmountsSummed === 0}
              className='slds-m-right_medium'
            />
            <div className='deduction-payments__subheading slds-m-top_xxx-small'>
              Exclude all payments from this deduction
            </div>
          </div>
          <Select
            label='Reason'
            placeholder='Choose'
            value={this.state.reduceAllPaymentsReason}
            options={[
              'Reduced wage - weather',
              'Reduced wage - illness',
              'Reduced wage - non-attendance',
              'Reduced wage - public holiday(s)',
              'Reduced/No wage - holiday',
              'Contract break',
              'Excessive farm deductions',
              'Other'
            ]}
            onChange={(e) => {
              this.reduceAll(payments)
              this.setState({
                reduceAllPaymentsReason: e.target.value
              }, () => this.handleReduceAllReasonChange(payments, this.state.reduceAllPaymentsReason))
            }}
            disabled={!this.state.reduceAllPayments}
            className='slds-m-left_xx-large'
          />
        </div>
        <div>
          <IdentityPaymentsEdit
            heading='Contributions by employee'
            payments={paymentsByEmployee}
            payrollCycleId={this.props.payrollCycleId}
            showProductWarning={this.state.reduceAllPayments}
            populateAmountInputsWithZero={this.state.reduceAllPayments}
            getReasonForChange={payment => this.state.paymentsToSave[payment]
              ? this.state.paymentsToSave[payment].reason
              : undefined
            }
            getPaymentAmountChanged={payment => !!this.state.paymentsToSave[payment]}
            displayResetToInitial={!!paymentsByEmployee.find(this.getNonZeroChangedPayment)} // !! is used because .find either returns value of the first element in array that satisfies function or undefined if none found, and we want boolean
            onAmountChange={(amount, payment, bulk) => this.handlePaymentAmountChange(amount, payment, bulk)}
            onReasonChange={(reason, payment, bulk) => this.handlePaymentReasonChange(reason, payment, bulk)}
            onResetToInitial={(payments) => payments
              ? this.resetToInitialAmounts(payments, false)
              : this.resetToInitialAmounts(paymentsByEmployee.filter(this.getNonZeroChangedPayment))
            }
          />
          <IdentityPaymentsEdit
            heading='Contributions by employer'
            payments={paymentsByEmployer}
            payrollCycleId={this.props.payrollCycleId}
            showProductWarning={this.state.reduceAllPayments}
            populateAmountInputsWithZero={this.state.reduceAllPayments}
            getReasonForChange={payment => this.state.paymentsToSave[payment]
              ? this.state.paymentsToSave[payment].reason
              : undefined
            }
            getPaymentAmountChanged={payment => !!this.state.paymentsToSave[payment]}
            displayResetToInitial={!!paymentsByEmployer.find(this.getNonZeroChangedPayment)}
            onAmountChange={(amount, payment, bulk) => this.handlePaymentAmountChange(amount, payment, bulk)}
            onReasonChange={(reason, payment, bulk) => this.handlePaymentReasonChange(reason, payment, bulk)}
            onResetToInitial={(payments) => payments
              ? this.resetToInitialAmounts(payments, false)
              : this.resetToInitialAmounts(paymentsByEmployer.filter(this.getNonZeroChangedPayment))
            }
            className='slds-m-top_large'
          />
        </div>
      </div>
    )
  }
}

const getWorkflowRunIdentityPayments = gql`
  query ($identity: UUID, $deduction: UUID) {
    payments(identityFor_Id: $identity, workflowrunDeducting_Id: $deduction, status: "pending", orderBy: "identity_for__last_name") {
      edges {
        node {
          id
          prod {
            id
            name
          }
  				amount
          originalAmount
          source
          state
          status
          statusReason
          editable
          zeroable
          identityFor {
            id
            identityValue
            employee {
              id
              employeeNumber
            }
          }
          workflowrunDeducting {
            id
            parameters
          }
          transactedIn {
            id
          }
        }
      }
    }
  }
`

const savePayment = gql`
  mutation savePayment($input: PaymentMutationInput!) {
    paymentMutate(input: $input) {
      payment {
        id
        amount
        statusReason
        state
      }
    }
  }
`

const getDeduction = gql`
  query ($id: ID!) {
    workflowrun(id: $id) {
      id
      paymentsDeducted {
        edges {
          node {
            id
            paymentSummary
            parentTotalAmount
          }
        }
      }
    }
  }
`

const withPaymentMutate =
  graphql(savePayment, {
    name: 'savePayment',
    options: ({ deduction }) => ({
      refetchQueries: [{
        query: getDeduction,
        variables: {
          id: deduction
        }
      }]
    })
  })

const withData =
  graphql(getWorkflowRunIdentityPayments, {
    options: ({ deductionId, identityId }) => ({
      variables: {
        deduction: decodeBase64(deductionId),
        identity: decodeBase64(identityId)
      }
    })
  })

export default compose(
  withData,
  withPaymentMutate
)(DeductionIdentityPaymentsEdit)



// WEBPACK FOOTER //
// ./src/components/deduction-identity-payments-edit/index.js